// Index hero carousel background video playback -- SG 20260817
//
// Playback is not paused by hover, tap or focus. The only user-controlled stop is the OS
// reduced-motion setting; playback otherwise pauses only when the video leaves the
// viewport or its slide goes inactive. Interaction-based pausing was removed pending a
// decision on the preferred pause affordance -- SG 20260818
const DESKTOP_BREAKPOINT = "(min-width: 990px)";

export class HeroCarouselVideo extends HTMLElement {
  private video: HTMLVideoElement | null = null;
  private sectionRoot: HTMLElement | null = null;
  private slideObserver: MutationObserver | null = null;
  private intersectionObserver: IntersectionObserver | null = null;
  private desktopMq: MediaQueryList;
  private reducedMotionMq: MediaQueryList;
  private isCarouselSlide = false;
  private isSlideActive = false;
  private isInView = false;
  private sourcesLoaded = false;
  private onDesktopMqChange: () => void;
  private onReducedMotionChange: () => void;
  private onPlaying: () => void;

  constructor() {
    super();
    this.desktopMq = window.matchMedia(DESKTOP_BREAKPOINT);
    this.reducedMotionMq = window.matchMedia("(prefers-reduced-motion: reduce)");
    this.onDesktopMqChange = () => {
      this.unloadSources();
      this.updatePlayback();
    };
    this.onReducedMotionChange = () => {
      this.updatePlayback();
    };
    this.onPlaying = () => {
      this.classList.add("is-playing");
    };
  }

  connectedCallback(): void {
    this.video = this.querySelector("video");
    this.sectionRoot = this.closest(".index__hero-carousel");
    const splideSlide = this.closest(".splide__slide");

    if (splideSlide) {
      this.isCarouselSlide = true;
      this.isSlideActive = splideSlide.classList.contains("is-active");
      this.slideObserver = new MutationObserver(() => {
        const wasActive = this.isSlideActive;
        this.isSlideActive = splideSlide.classList.contains("is-active");
        if (wasActive !== this.isSlideActive) {
          this.updatePlayback();
        }
      });
      this.slideObserver.observe(splideSlide, {
        attributes: true,
        attributeFilter: ["class"],
      });
    } else {
      this.isSlideActive = true;
    }

    if (this.video) {
      this.video.addEventListener("playing", this.onPlaying);
    }

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        this.isInView = Boolean(entry?.isIntersecting);
        this.updatePlayback();
      },
      { threshold: 0.25 }
    );
    this.intersectionObserver.observe(this);

    this.desktopMq.addEventListener("change", this.onDesktopMqChange);
    this.reducedMotionMq.addEventListener("change", this.onReducedMotionChange);

    this.updatePlayback();
  }

  disconnectedCallback(): void {
    this.slideObserver?.disconnect();
    this.intersectionObserver?.disconnect();
    this.desktopMq.removeEventListener("change", this.onDesktopMqChange);
    this.reducedMotionMq.removeEventListener("change", this.onReducedMotionChange);

    if (this.video) {
      this.video.removeEventListener("playing", this.onPlaying);
      this.pauseVideo();
    }
  }

  private get videoOnMobile(): boolean {
    return this.dataset.videoOnMobile !== "false";
  }

  private shouldPlay(): boolean {
    if (!this.video || this.reducedMotionMq.matches) {
      return false;
    }
    if (!this.isSlideActive || !this.isInView) {
      return false;
    }
    if (!this.desktopMq.matches && !this.videoOnMobile) {
      return false;
    }
    return true;
  }

  private unloadSources(): void {
    if (!this.video) {
      return;
    }

    for (const source of Array.from(this.video.querySelectorAll("source"))) {
      source.removeAttribute("src");
    }

    this.video.removeAttribute("src");
    this.video.load();
    this.sourcesLoaded = false;
    this.classList.remove("is-playing");
  }

  private loadSources(): void {
    if (!this.video || this.sourcesLoaded) {
      return;
    }

    const targetMedia = this.desktopMq.matches ? "desktop" : "mobile";
    let loaded = false;

    for (const source of Array.from(this.video.querySelectorAll("source"))) {
      const sourceMedia = source.getAttribute("data-media");
      const deferredSrc = source.getAttribute("data-src");

      if (!deferredSrc) {
        continue;
      }

      if (sourceMedia === targetMedia) {
        source.setAttribute("src", deferredSrc);
        loaded = true;
      } else {
        source.removeAttribute("src");
      }
    }

    if (loaded) {
      this.video.load();
      this.sourcesLoaded = true;
    }
  }

  private pauseOthers(): void {
    if (!this.sectionRoot) {
      return;
    }

    for (const element of Array.from(this.sectionRoot.querySelectorAll("hero-carousel-video"))) {
      if (element !== this) {
        (element as HeroCarouselVideo).pauseVideo();
      }
    }
  }

  pauseVideo(): void {
    if (!this.video) {
      return;
    }

    this.video.pause();
  }

  private playVideo(): void {
    if (!this.video) {
      return;
    }

    this.video.muted = true;
    this.pauseOthers();

    const playPromise = this.video.play();
    if (playPromise !== undefined) {
      playPromise.catch(() => {
        this.classList.remove("is-playing");
      });
    }
  }

  private updatePlayback(): void {
    if (!this.shouldPlay()) {
      this.pauseVideo();
      if (this.isCarouselSlide && !this.isSlideActive) {
        this.unloadSources();
      }
      return;
    }

    this.loadSources();

    if (this.sourcesLoaded) {
      this.playVideo();
    }
  }
}

customElements.define("hero-carousel-video", HeroCarouselVideo);
